import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  apiApproveLeaveRequest,
  apiRejectLeaveRequest,
  type LeaveRequest,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { getLeaveApiErrorMessage } from "@/lib/leave-i18n";

interface LeaveApprovalButtonsProps {
  requestId: number;
  employeeId?: number;
  onSuccess?: () => void;
  fullWidth?: boolean;
}

export function LeaveApprovalButtons({
  requestId,
  employeeId,
  onSuccess,
  fullWidth = false,
}: LeaveApprovalButtonsProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [rejectReason, setRejectReason] = useState("");
  const [rejectExpanded, setRejectExpanded] = useState(false);

  function applyUpdate(updated: LeaveRequest) {
    // Immediately update the cached single-request entry so the modal shows the new status
    queryClient.setQueryData(["leaveRequest", String(requestId)], updated);
    queryClient.setQueryData(["leaveRequest", requestId], updated);
    // Invalidate all list caches so counts and lists refresh
    queryClient.invalidateQueries({ queryKey: ["leaveRequests"] });
    queryClient.invalidateQueries({ queryKey: ["myLeaveRequests"] });
    queryClient.invalidateQueries({ queryKey: ["myLeaveBalance"] });
    queryClient.invalidateQueries({ queryKey: ["notificationCount"] });
  }

  const approveMutation = useMutation({
    mutationFn: () => apiApproveLeaveRequest(requestId),
    onSuccess: (updated) => {
      toast.success(t("leave.requestApproved"));
      applyUpdate(updated);
      onSuccess?.();
    },
    onError: (err: Error) => toast.error(getLeaveApiErrorMessage(t, err)),
  });

  const rejectMutation = useMutation({
    mutationFn: () => apiRejectLeaveRequest(requestId, rejectReason.trim()),
    onSuccess: (updated) => {
      toast.success(t("leave.requestRejected"));
      setRejectExpanded(false);
      setRejectReason("");
      applyUpdate(updated);
      onSuccess?.();
    },
    onError: (err: Error) => toast.error(getLeaveApiErrorMessage(t, err)),
  });

  const isPending = approveMutation.isPending || rejectMutation.isPending;
  const canConfirm = rejectReason.trim().length >= 10;
  const isOwnRequest =
    employeeId !== undefined &&
    user?.employeeId !== undefined &&
    String(employeeId) === String(user.employeeId);

  function guardOwnRequest() {
    if (isOwnRequest) {
      toast.error(t("leave.errors.selfApprovalRejection"));
      return true;
    }
    return false;
  }

  // ── Inline rejection form — avoids nested portal/dialog conflicts ──
  if (rejectExpanded) {
    return (
      <div className={fullWidth ? "w-full space-y-2" : "space-y-2 w-full"}>
        <Textarea
          placeholder={t("leave.rejectionReasonPlaceholder")}
          rows={3}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          autoFocus
        />
        <p className="text-xs text-muted-foreground text-right">
          {rejectReason.trim().length} / 10{" "}
          {t("leave.minCharsRequired")}
        </p>
        <div
          className={
            fullWidth ? "grid grid-cols-2 gap-3" : "flex items-center gap-2"
          }
        >
          <Button
            size={fullWidth ? "default" : "sm"}
            variant="outline"
            className={fullWidth ? "w-full" : undefined}
            onClick={() => {
              setRejectExpanded(false);
              setRejectReason("");
            }}
            disabled={isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button
            size={fullWidth ? "default" : "sm"}
            variant="destructive"
            className={fullWidth ? "w-full" : undefined}
            disabled={!canConfirm || isPending}
            onClick={() => {
              if (!guardOwnRequest()) rejectMutation.mutate();
            }}
          >
            {rejectMutation.isPending
              ? t("leave.rejecting")
              : t("leave.confirmRejection")}
          </Button>
        </div>
      </div>
    );
  }

  // ── Default: Approve + Reject buttons ──
  return (
    <div
      className={
        fullWidth ? "grid grid-cols-2 gap-3 w-full" : "flex items-center gap-2"
      }
    >
      {/* Approve */}
      <Button
        size={fullWidth ? "default" : "sm"}
        className={
          fullWidth
            ? "gradient-brand text-primary-foreground w-full"
            : undefined
        }
        onClick={() => {
          if (!guardOwnRequest()) approveMutation.mutate();
        }}
        disabled={isPending}
      >
        <Check className="h-4 w-4 mr-1.5" />
        {approveMutation.isPending
          ? t("leave.approving")
          : t("leave.approveRequest")}
      </Button>

      {/* Reject — expands inline form, no nested dialog */}
      <Button
        size={fullWidth ? "default" : "sm"}
        variant="destructive"
        className={fullWidth ? "w-full" : undefined}
        disabled={isPending}
        onClick={() => {
          if (!guardOwnRequest()) setRejectExpanded(true);
        }}
      >
        <X className="h-4 w-4 mr-1.5" />
        {t("leave.rejectRequest")}
      </Button>
    </div>
  );
}
